import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { FileText, Upload, Trash2, ExternalLink, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentUploadCard = ({ 
  docType,
  docConfig,
  activeDocument, 
  onUpload, 
  uploading,
  language,
  baseUrl
}) => {
  const fileInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(language === 'ro' ? 'Doar fișiere PDF sunt permise' : 'Only PDF files are allowed');
      return;
    }

    await onUpload(file, docType);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const publicUrl = `${baseUrl}/${docConfig.slug}`;
  const label = language === 'ro' ? docConfig.label_ro : docConfig.label_en;

  return (
    <Card className="overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${activeDocument ? 'bg-green-500' : 'bg-amber-500'}`} />
                <CardTitle className="text-base font-medium">{label}</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                {activeDocument && (
                  <Badge variant="outline" className="text-xs">
                    {activeDocument.original_filename}
                  </Badge>
                )}
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-4">
              {/* Status */}
              {activeDocument ? (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600 dark:text-green-400">
                      {language === 'ro' ? 'Document activ' : 'Active document'}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(publicUrl, '_blank')}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    {language === 'ro' ? 'Vezi' : 'View'}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <FileText className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-600 dark:text-amber-400">
                    {language === 'ro' ? 'Niciun document încărcat' : 'No document uploaded'}
                  </span>
                </div>
              )}

              {/* Public URL */}
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">URL:</p>
                <code className="text-xs bg-background px-2 py-1 rounded border block overflow-x-auto">
                  {publicUrl}
                </code>
              </div>

              {/* Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="w-3 h-3 mr-2" />
                {uploading 
                  ? (language === 'ro' ? 'Se încarcă...' : 'Uploading...') 
                  : (language === 'ro' ? 'Încarcă PDF' : 'Upload PDF')}
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

const Documents = () => {
  const { t, language } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, typesRes] = await Promise.all([
        axios.get(`${API}/admin/documents`),
        axios.get(`${API}/admin/document-types`)
      ]);
      setDocuments(docsRes.data);
      setDocumentTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, documentType) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/admin/documents/upload?document_type=${documentType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(language === 'ro' ? 'Document încărcat cu succes!' : 'Document uploaded successfully!');
      fetchData();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;
    try {
      await axios.delete(`${API}/admin/documents/${documentToDelete.id}`);
      toast.success(language === 'ro' ? 'Document șters cu succes!' : 'Document deleted successfully!');
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error.response?.data?.detail || 'Error deleting document');
    }
  };

  const getBaseUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return backendUrl.replace('/api', '').replace(/\/$/, '');
  };

  const getActiveDocument = (docType) => {
    return documents.find(doc => doc.is_active && doc.type === docType);
  };

  const getDocumentTypeLabel = (type) => {
    const config = documentTypes[type];
    if (config) {
      return language === 'ro' ? config.label_ro : config.label_en;
    }
    return type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="documents-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const baseUrl = getBaseUrl();

  // Group document types
  const subscriptionDocs = ['terms_simple', 'terms_unlimited'];
  const legalDocs = ['terms_conditions', 'privacy_policy', 'delivery_policy', 'return_policy', 'fiscal_data'];

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
          {language === 'ro' ? 'Documente' : 'Documents'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {language === 'ro' 
            ? 'Gestionează documentele legale și termenii pentru abonamente' 
            : 'Manage legal documents and subscription terms'}
        </p>
      </div>

      {/* Subscription Documents */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">
          {language === 'ro' ? 'Documente Abonamente' : 'Subscription Documents'}
        </h2>
        <div className="grid gap-3">
          {subscriptionDocs.map(docType => (
            documentTypes[docType] && (
              <DocumentUploadCard
                key={docType}
                docType={docType}
                docConfig={documentTypes[docType]}
                activeDocument={getActiveDocument(docType)}
                onUpload={handleUpload}
                uploading={uploading}
                language={language}
                baseUrl={baseUrl}
              />
            )
          ))}
        </div>
      </div>

      {/* Legal Documents */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">
          {language === 'ro' ? 'Documente Legale' : 'Legal Documents'}
        </h2>
        <div className="grid gap-3">
          {legalDocs.map(docType => (
            documentTypes[docType] && (
              <DocumentUploadCard
                key={docType}
                docType={docType}
                docConfig={documentTypes[docType]}
                activeDocument={getActiveDocument(docType)}
                onUpload={handleUpload}
                uploading={uploading}
                language={language}
                baseUrl={baseUrl}
              />
            )
          ))}
        </div>
      </div>

      {/* Documents History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {language === 'ro' ? 'Istoric documente' : 'Documents history'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {documents.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{language === 'ro' ? 'Nume fișier' : 'Filename'}</TableHead>
                  <TableHead>{language === 'ro' ? 'Tip' : 'Type'}</TableHead>
                  <TableHead>{language === 'ro' ? 'Data încărcării' : 'Upload date'}</TableHead>
                  <TableHead>{t('status')}</TableHead>
                  <TableHead className="text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-red-500" />
                        <span className="truncate max-w-[200px]">{doc.original_filename}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {getDocumentTypeLabel(doc.type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(doc.uploaded_at)}
                    </TableCell>
                    <TableCell>
                      {doc.is_active ? (
                        <Badge variant="default">{t('active')}</Badge>
                      ) : (
                        <Badge variant="secondary">{t('inactive')}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDocumentToDelete(doc);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-document-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p>{language === 'ro' ? 'Niciun document încărcat' : 'No documents uploaded'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-document-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {documentToDelete?.original_filename}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-document">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Documents;
